import { cn } from '@/lib/cn';
import { Card } from '@/ui/Card';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import type { Alerta, AlertaDelTablero } from './api';

/**
 * piezas.tsx — las partes de la Analítica: el indicador, las alertas, el
 * reparto por área y el gráfico de meses.
 *
 * ## Dos defectos heredados viven aquí
 *
 * - **El tinte de las alertas nunca se pintó.** El vanilla las escribía con
 *   `background:${sev[0]}12`, que produce `background: var(--red-text)12` —no
 *   es un color válido, el navegador lo descarta—. Llevaba así desde que se
 *   escribió: se veía el filete de la izquierda y el fondo transparente.
 * - **Los dos gráficos SVG no tenían alternativa textual.** Un `<svg>` sin
 *   `role` ni título y con los números solo como altura de barra: para un
 *   lector de pantalla, nada. Aquí el mismo dato va además en una tabla que
 *   solo él ve.
 */

const TINTA: Record<string, string> = {
  bueno: 'text-ok-ink',
  alerta: 'text-warn-ink',
  malo: 'text-danger-ink',
};

/** Un indicador.
 *
 *  `estado` es `null`, `bueno`, `alerta` o `malo` — **no un color**. Es la
 *  decisión que el vanilla ya había tomado y que se conserva tal cual: antes
 *  cada llamada pasaba un hexadecimal y el resultado eran treinta y un
 *  indicadores de colores repartidos por criterio decorativo, con lo que el
 *  color no servía para saber dónde mirar. */
export function Indicador({
  icono, estado, valor, etiqueta, nota,
}: {
  icono: string;
  estado?: 'bueno' | 'alerta' | 'malo' | null;
  valor: string | number;
  etiqueta: string;
  nota?: string;
}) {
  const tinta = estado ? TINTA[estado] : undefined;
  return (
    <Card className="flex flex-col gap-0.5 p-4">
      <p className="flex items-center gap-2">
        <i
          className={cn('bi text-5', icono, tinta ?? 'text-muted')}
          aria-hidden="true"
        />
        <strong className={cn('text-7 font-extrabold leading-none tabular-nums', tinta ?? 'text-brand-ink')}>
          {valor}
        </strong>
      </p>
      <p className="text-2 font-semibold text-brand-ink">{etiqueta}</p>
      {nota && <p className="text-1 text-muted">{nota}</p>}
    </Card>
  );
}

export function Indicadores({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(10rem,1fr))]">
      {children}
    </div>
  );
}

export function Titulo({ icono, children }: { icono: string; children: React.ReactNode }) {
  return (
    <h2 className="mb-2 flex items-center gap-2 text-4 font-bold text-brand-ink">
      <i className={cn('bi', icono)} aria-hidden="true" /> {children}
    </h2>
  );
}

/* ── Alertas ───────────────────────────────────────────────────────────── */

const SEVERIDAD = {
  danger: { tinte: 'border-danger/40 bg-danger/12', tinta: 'text-danger-ink', icono: 'bi-exclamation-octagon-fill' },
  warn: { tinte: 'border-warn/40 bg-warn/16', tinta: 'text-warn-ink', icono: 'bi-exclamation-triangle-fill' },
  ok: { tinte: 'border-ok/40 bg-ok/12', tinta: 'text-ok-ink', icono: 'bi-check-circle-fill' },
  info: { tinte: 'border-info/40 bg-info/12', tinta: 'text-info-ink', icono: 'bi-info-circle-fill' },
} as const;

type Severidad = keyof typeof SEVERIDAD;

const DE_LA_ANALITICA: Record<string, Severidad> = {
  high: 'danger', medium: 'warn', ok: 'ok',
};

/** Las alertas de Aprendizaje y Cultura: un texto y su gravedad. */
export function ListaDeAlertas({ alertas }: { alertas: Alerta[] }) {
  if (!alertas.length) {
    return <p className="text-3 text-muted">Sin alertas.</p>;
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {alertas.map((a, i) => {
        const s = SEVERIDAD[DE_LA_ANALITICA[a.severity] ?? 'info'];
        return (
          <li
            key={i}
            className={cn('flex items-center gap-2 rounded-sm border px-3 py-2 text-3', s.tinte, s.tinta)}
          >
            <i className={cn('bi shrink-0', s.icono)} aria-hidden="true" />
            <span className="min-w-0 text-ink">{a.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Las del tablero de Bienestar, que además llevan a la pantalla donde se
 *  arregla lo que avisan. Son enlaces porque eso es lo que son. */
export function AlertasAccionables({ alertas }: { alertas: AlertaDelTablero[] }) {
  if (!alertas.length) {
    return (
      <p className="flex items-center gap-2 rounded-sm border border-ok/40 bg-ok/12 px-3 py-2 text-3 text-ok-ink">
        <i className="bi bi-check-circle-fill" aria-hidden="true" /> Sin alertas: todo al día.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {alertas.map((a, i) => {
        const s = SEVERIDAD[(a.sev as Severidad) in SEVERIDAD ? (a.sev as Severidad) : 'info'];
        return (
          <li key={i}>
            <a
              href={`#${a.page}`}
              className={cn(
                'flex items-center gap-3 rounded-sm border px-3 py-2.5',
                'hover:shadow-raised focus-visible:focus-anillo',
                s.tinte,
              )}
            >
              <i className={cn('bi shrink-0 text-5', a.icon, s.tinta)} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <strong className="block text-3 text-brand-ink">{a.title}</strong>
                <span className="block text-2 text-ink-2">{a.detail}</span>
              </span>
              <i className="bi bi-chevron-right shrink-0 text-muted" aria-hidden="true" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/* ── Repartos ──────────────────────────────────────────────────────────── */

/** Una fila de «por área»: su nombre, su número escrito y su barra. */
export function FilaDeArea({
  nombre, pct, derecha, nota, estado,
}: {
  nombre: string;
  pct: number;
  derecha: React.ReactNode;
  nota?: string;
  estado?: 'bueno' | 'alerta' | 'malo' | null;
}) {
  return (
    <li>
      <p className="mb-0.5 flex items-baseline justify-between gap-2 text-2">
        <span className="min-w-0 truncate text-ink-2">{nombre}</span>
        <strong className={cn('shrink-0 tabular-nums', estado ? TINTA[estado] : 'text-ink')}>
          {derecha}
        </strong>
      </p>
      <BarraDeProgreso pct={pct} etiqueta={`Avance de ${nombre}`} />
      {nota && <p className="mt-0.5 text-1 text-muted">{nota}</p>}
    </li>
  );
}

/* ── El gráfico de meses ───────────────────────────────────────────────── */

export interface SerieDeMeses {
  label: string;
  a: number;
  b: number;
}

/**
 * Dos barras por mes, en SVG y sin dependencias, como el vanilla.
 *
 * Lo que cambia es que **el dato también está escrito**: el `<svg>` es
 * `role="img"` con su título, y debajo va la misma serie en una tabla que solo
 * ve un lector de pantalla. Antes los números solo existían como altura.
 */
export function GraficoDeMeses({
  serie, nombreA, nombreB, titulo,
}: {
  serie: SerieDeMeses[];
  nombreA: string;
  nombreB: string;
  titulo: string;
}) {
  const ancho = 640;
  const alto = 180;
  const izq = 28;
  const abajo = 26;
  const arriba = 10;
  const maximo = Math.max(1, ...serie.map((s) => Math.max(s.a, s.b)));
  const porMes = (ancho - izq) / Math.max(serie.length, 1);
  const anchoBarra = Math.min(18, porMes / 3);
  const y = (v: number) => arriba + (alto - arriba - abajo) * (1 - v / maximo);

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        width="100%"
        role="img"
        aria-label={titulo}
        className="overflow-visible"
      >
        <title>{titulo}</title>
        {[0, 0.5, 1].map((f) => {
          const valor = Math.round(maximo * f);
          const yy = y(valor);
          return (
            <g key={f}>
              <line x1={izq} y1={yy} x2={ancho} y2={yy} className="stroke-line" />
              <text x={0} y={yy + 3} fontSize="9" className="fill-muted">{valor}</text>
            </g>
          );
        })}
        {serie.map((s, i) => {
          const centro = izq + porMes * i + porMes / 2;
          return (
            <g key={s.label}>
              <rect
                x={centro - anchoBarra - 1} y={y(s.a)} width={anchoBarra}
                height={alto - abajo - y(s.a)} rx="2" className="fill-brand"
              />
              <rect
                x={centro + 1} y={y(s.b)} width={anchoBarra}
                height={alto - abajo - y(s.b)} rx="2" className="fill-accent"
              />
              <text
                x={centro} y={alto - abajo + 14} fontSize="9"
                textAnchor="middle" className="fill-muted"
              >
                {s.label}
              </text>
            </g>
          );
        })}
      </svg>

      <figcaption className="mt-2 flex flex-wrap justify-center gap-4 text-2 text-muted">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-xs bg-brand" aria-hidden="true" /> {nombreA}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-xs bg-accent" aria-hidden="true" /> {nombreB}
        </span>
      </figcaption>

      {/* La misma serie, para quien no ve el gráfico. */}
      <table className="sr-only">
        <caption>{titulo}</caption>
        <thead>
          <tr><th scope="col">Mes</th><th scope="col">{nombreA}</th><th scope="col">{nombreB}</th></tr>
        </thead>
        <tbody>
          {serie.map((s) => (
            <tr key={s.label}>
              <th scope="row">{s.label}</th>
              <td>{s.a}</td>
              <td>{s.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
